import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const SubscriptionBanner = () => {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  
  // Clients don't need subscriptions - they just browse and buy
  const accountType = user?.accountType || 'farmer';
  const requiresSubscription = accountType === 'farmer' || accountType === 'supplier';
  
  // Redirect to appropriate billing page based on account type
  const billingUrl = accountType === 'supplier' ? '/supplier/settings' : '/billing';

  useEffect(() => {
    const handleExpired = () => {
      // Only show if not dismissed in this session AND account requires subscription
      if (!dismissed && requiresSubscription) {
        setShow(true);
      }
    };

    window.addEventListener('subscription-expired', handleExpired);
    return () => window.removeEventListener('subscription-expired', handleExpired);
  }, [dismissed, requiresSubscription]);

  const handleDismiss = () => {
    setShow(false);
    setDismissed(true);
    // Re-show after 5 minutes if still navigating
    setTimeout(() => setDismissed(false), 5 * 60 * 1000);
  };

  if (!show) return null;

  return (
    <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
      {/* Mobile Layout */}
      <div className="block sm:hidden px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Subscription Expired</p>
            <p className="text-xs text-amber-100 mt-0.5">
              Renew to access all features
            </p>
          </div>
          <button 
            onClick={handleDismiss}
            className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Dismiss"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <Link 
          to={billingUrl} 
          className="mt-2 block w-full text-center px-4 py-2 bg-white text-amber-600 font-semibold text-sm rounded-lg hover:bg-amber-50 transition-colors"
        >
          Renew Now — KES 1,000/month
        </Link>
      </div>

      {/* Desktop/Tablet Layout */}
      <div className="hidden sm:block px-4 py-3">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 p-2 bg-white/20 rounded-full">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div>
              <p className="font-semibold">Subscription Expired</p>
              <p className="text-sm text-amber-100">
                Your access is limited. Renew now to continue managing your {accountType === 'supplier' ? 'products and orders' : 'farm'}.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link 
              to={billingUrl} 
              className="px-5 py-2 bg-white text-amber-600 font-semibold rounded-lg hover:bg-amber-50 transition-colors shadow-sm"
            >
              Renew Now
            </Link>
            <button 
              onClick={handleDismiss}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
